import axios from 'axios';
import { API_BASE_URL } from '../config';

export interface QuickbooksProduct {
  id: string;
  name: string;
  description?: string;
  basePrice: number;
  isActive: boolean;
}

export interface QuickbooksInvoice {
  customerRef: {
    value: string;
  };
  line: Array<{
    detailType: string;
    amount: number;
    salesItemLineDetail: {
      itemRef: {
        value: string;
      };
      qty: number;
      unitPrice: number;
    };
  }>;
  shippingAddr?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
}

class QuickbooksService {
  private baseUrl = `${API_BASE_URL}/quickbooks`;

  // Product Management
  async createProduct(product: Omit<QuickbooksProduct, 'id'>): Promise<string> {
    const response = await axios.post(`${this.baseUrl}/items`, product);
    return response.data.id;
  }

  async updateProduct(id: string, product: Partial<QuickbooksProduct>): Promise<void> {
    await axios.put(`${this.baseUrl}/items/${id}`, product);
  }

  async deleteProduct(id: string): Promise<void> {
    await axios.delete(`${this.baseUrl}/items/${id}`);
  }

  async getProduct(id: string): Promise<QuickbooksProduct> {
    const response = await axios.get(`${this.baseUrl}/items/${id}`);
    return response.data;
  }

  async getAllProducts(): Promise<QuickbooksProduct[]> {
    const response = await axios.get(`${this.baseUrl}/items`);
    return response.data;
  }

  // Invoice Management
  async createInvoice(invoice: QuickbooksInvoice): Promise<string> {
    const response = await axios.post(`${this.baseUrl}/invoices`, invoice);
    return response.data.id;
  }

  // Error Handling Helper
  private handleError(error: any): never {
    if (error.response) {
      // Server responded with error status
      throw new Error(error.response.data.message || 'Server error occurred');
    } else if (error.request) {
      // Request made but no response received
      throw new Error('No response received from server');
    } else {
      // Error in request setup
      throw new Error(error.message || 'Error setting up request');
    }
  }

  // Account Management
  async getExpenseAccounts(): Promise<{
    accounts: Array<{
      id: string;
      name: string;
      type: string;
      isSelected: boolean;
    }>;
    selectedAccountId: string | null;
  }> {
    try {
      const response = await axios.get(`${this.baseUrl}/expense-accounts`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async setExpenseAccount(accountId: string): Promise<{
    message: string;
    accountId: string;
    accountName: string;
  }> {
    try {
      const response = await axios.put(`${this.baseUrl}/expense-account`, { accountId });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  // Token Status & Management
  async getTokenStatus(): Promise<{ 
    tokenExists: boolean; 
    isValid: boolean; 
    isExpired: boolean; 
    expiresAt: string | null;
    accessToken: string | null;
    refreshToken: string | null;
    realmId: string | null;
  }> {
    try {
      const response = await axios.get(`${this.baseUrl}/token-status`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async refreshToken(): Promise<{ message: string; expiresAt: string }> {
    try {
      const response = await axios.get(`${this.baseUrl}/refresh-token`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async clearToken(): Promise<{ message: string }> {
    try {
      const response = await axios.delete(`${this.baseUrl}/clear-token`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async createToken(tokenData: { accessToken: string; refreshToken: string; expiresIn: number }): Promise<{ message: string; tokenId: number; expiresAt: string }> {
    try {
      const response = await axios.post(`${this.baseUrl}/create-token`, tokenData);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  // Sync products with QuickBooks
  async syncAllProducts(): Promise<{ message: string }> {
    try {
      // This endpoint is in the ProductsController, not QuickbooksController
      const response = await axios.post(`${API_BASE_URL}/products/sync-quickbooks`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  // Sync a single product with QuickBooks
  async syncSingleProduct(productId: number): Promise<{ success: boolean; message: string; isServerError?: boolean; details?: string }> {
    try {
      // This endpoint is in the ProductsController, not QuickbooksController
      const response = await axios.post(`${API_BASE_URL}/products/sync-quickbooks/${productId}`);
      return response.data;
    } catch (error: any) {
      // Check if this is a server error from QuickBooks API
      if (error.response?.data?.isServerError) {
        // Return the server error information directly
        return {
          success: false,
          message: error.response.data.message || 'QuickBooks API is currently unavailable. Please try again later.',
          isServerError: true,
          details: error.response.data.details
        };
      }
      
      // For other errors, use the standard error handling
      this.handleError(error);
    }
  }

  // Get QuickBooks customers
  async getCustomers(): Promise<Array<{
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    address: string | null;
  }>> {
    try {
      const response = await axios.get(`${this.baseUrl}/customers`);
      return response.data.customers;
    } catch (error) {
      this.handleError(error);
    }
  }

  // Note: initiateAuth is handled by redirecting the browser, not an API call here.
}

export const quickbooksService = new QuickbooksService();
